import React, { useState } from "react";
import { useUncontrolled } from "@mantine/hooks";
import {
  Alert,
  Button,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconChevronRight,
  IconInfoCircle,
} from "@tabler/icons-react";
import { Employee } from "../../../types/salon";
import { CommissionRecord } from "../../../types/commission";
import { getActiveCommission } from "../../../utils/commissionUtils";
import {
  StandardTable,
  StandardTableColumn,
} from "../../common/StandardTable/StandardTable";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { StaffIdentity } from "../../common/StaffIdentity";
import CommissionRateDrawer, {
  CommissionDrawerRequest,
} from "./CommissionRateDrawer";
import { MethodBadge, formatEffectiveDate } from "./commissionDisplay";
import { useStaffCommissions } from "./useStaffCommissions";

const StaffCell: React.FC<{ employee: Employee }> = ({ employee }) => (
  <StaffIdentity
    firstName={employee.first_name}
    lastName={employee.last_name}
    title={employee.title}
    photoUrl={employee.staff_photo_url ?? employee.avatar_url}
  />
);

export interface CommissionRatesTableProps {
  /** Controlled drawer, so the page header's "Add Rate" can open the add
   * form. Omit both to let the table manage the drawer itself. */
  drawerRequest?: CommissionDrawerRequest | null;
  onDrawerRequestChange?: (request: CommissionDrawerRequest | null) => void;
}

/**
 * Commissions › Commission Rates (flag-gated redesign). One flat row per staff
 * member — the method and effective date of the rate in force, or "No
 * commission structure set". Clicking a row opens the Commission Rate drawer
 * with the full current rate and past rates; adding, editing and deleting a
 * rate happen there too.
 */
const CommissionRatesTable: React.FC<CommissionRatesTableProps> = ({
  drawerRequest,
  onDrawerRequestChange,
}) => {
  const data = useStaffCommissions();
  const [drawer, setDrawer] = useUncontrolled<CommissionDrawerRequest | null>({
    value: drawerRequest,
    defaultValue: null,
    finalValue: null,
    onChange: onDrawerRequestChange,
  });
  const [deleting, setDeleting] = useState<CommissionRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const recordsFor = (staffId: string | undefined) =>
    (staffId && data.commissionsByStaff[staffId]) || [];

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await data.deleteRecord(deleting.id);
    } catch (error: any) {
      console.error("Error deleting commission:", error);
      data.setLoadError(
        error?.message || "Failed to delete commission. Please try again.",
      );
    } finally {
      setDeleting(null);
      setIsDeleting(false);
    }
  };

  const columns: StandardTableColumn<Employee>[] = [
    {
      key: "staff",
      label: "Staff Member",
      render: (employee) => <StaffCell employee={employee} />,
    },
    {
      key: "method",
      label: "Method",
      render: (employee) => {
        const active = getActiveCommission(recordsFor(employee.id));
        return active ? (
          <MethodBadge commission={active} />
        ) : (
          <Text size="sm" c="dimmed">
            No commission structure set
          </Text>
        );
      },
    },
    {
      key: "effective",
      label: "Effective Since",
      render: (employee) => {
        const active = getActiveCommission(recordsFor(employee.id));
        return active ? (
          <Text size="sm">
            {formatEffectiveDate(active.effective_start_date)}
          </Text>
        ) : null;
      },
    },
    {
      key: "open",
      width: 40,
      align: "right",
      render: () => (
        <IconChevronRight size={16} color="var(--settings-ink-3)" aria-hidden />
      ),
    },
  ];

  if (data.isLoading) {
    return (
      <Stack pos="relative" mih={200}>
        <LoadingOverlay visible />
      </Stack>
    );
  }

  return (
    <Stack>
      {data.loadError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={() => data.setLoadError(null)}
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{data.loadError}</Text>
            <Button
              size="compact-xs"
              variant="light"
              color="red"
              onClick={data.retry}
            >
              Retry
            </Button>
          </Group>
        </Alert>
      )}

      {data.scope === "own" && (
        <Group justify="flex-end">
          <Tooltip
            label="You can only view and manage your own commission settings. Contact a manager to view other staff members' commissions."
            multiline
            w={280}
            withArrow
          >
            <IconInfoCircle
              size={20}
              style={{ color: "var(--mantine-color-blue-6)", cursor: "help" }}
            />
          </Tooltip>
        </Group>
      )}

      <StandardTable<Employee>
        columns={columns}
        data={data.employees}
        getRowKey={(employee) => employee.id}
        onRowClick={(employee) =>
          setDrawer({ staffId: employee.id, view: "details" })
        }
        ariaLabel="Staff commissions"
        emptyState={
          <Text c="dimmed" size="sm">
            No active staff members.
          </Text>
        }
      />

      <CommissionRateDrawer
        request={drawer}
        onClose={() => setDrawer(null)}
        employees={data.employees}
        commissionsByStaff={data.commissionsByStaff}
        canEdit={data.canEdit}
        onDelete={setDeleting}
        onSaved={data.refresh}
      />

      {data.canEdit && (
        <DeleteConfirmationModal
          isOpen={deleting !== null}
          onClose={() => setDeleting(null)}
          onConfirm={confirmDelete}
          title="Delete Commission"
          message="Are you sure you want to delete this commission record? This action cannot be undone."
          isLoading={isDeleting}
        />
      )}
    </Stack>
  );
};

export default CommissionRatesTable;
